/**
 * EventSignups — the people who came to this assembly's events.
 *
 * The bell only ever said "Takudzwa has registered for WARSAW", and there was
 * no screen where the assembly could see those people at all, let alone ask
 * them home. Inviting one records it against the welcome team's visitor list
 * and hands back a link to send; nobody is added to the roll and no
 * application is created — the person still applies.
 */
import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import {
  CalendarCheck, Check, ChevronDown, Copy, Link as LinkIcon, Loader2, Mail,
  Search, UserPlus,
} from 'lucide-react';
import { toast } from 'sonner';
import { authHeaders } from '@/lib/authToken';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const api = async (path, opts = {}) => {
  const res = await fetch(`${API_URL}${path}`, {
    ...opts,
    headers: { 'Content-Type': 'application/json', ...authHeaders(), ...(opts.headers || {}) },
  });
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || 'Request failed');
  return res.json();
};

const FILTERS = [
  { key: 'not_invited', label: 'Still to invite' },
  { key: 'invited', label: 'Invited' },
  { key: 'already_here', label: 'Already with us' },
  { key: 'all', label: 'Everyone' },
  { key: 'follow_up', label: 'Follow-up' },
];

const SignupRow = ({ row, churchId, onInvited, selected, onSelect }) => {
  const invite = useMutation({
    mutationFn: () => api(
      `/api/admin/churches/${churchId}/event-signups/${row.delegate_id}/invite`,
      { method: 'POST', body: JSON.stringify({ send_email: true }) }),
    onSuccess: (r) => {
      if (r.emailed) toast.success(`Invitation emailed to ${r.name}`);
      else toast.message(`Invitation recorded — ${r.reason || 'send the link yourself'}`, {
        description: r.link,
        action: {
          label: 'Copy link',
          onClick: () => navigator.clipboard?.writeText(r.link),
        },
      });
      onInvited();
    },
    onError: (e) => toast.error(e.message),
  });

  return (
    <div className="flex flex-wrap items-center gap-3 py-2.5 border-b border-slate-100 last:border-0"
         data-testid={`event-signup-row-${row.delegate_id}`}>
      {row.can_invite && (
        <Checkbox checked={selected} onCheckedChange={() => onSelect(row.delegate_id)}
                  data-testid={`event-signup-select-${row.delegate_id}`} />
      )}
      <div className="min-w-0 flex-1">
        <div className="font-medium text-slate-900 text-sm truncate">{row.name}</div>
        <div className="text-[11px] text-slate-500 truncate">
          {row.email || row.phone || 'no contact details'}
          {row.city ? ` · ${row.city}` : ''}
        </div>
      </div>

      <div className="min-w-0">
        <Badge variant="outline" className="text-[10px] font-normal max-w-[180px] truncate">
          <CalendarCheck className="h-3 w-3 mr-1 shrink-0" />{row.event_name}
        </Badge>
        {row.attended && (
          <span className="text-[10px] text-emerald-600 ml-1.5">checked in</span>
        )}
      </div>

      <div className="shrink-0">
        {row.already_member ? (
          <span className="text-[11px] text-slate-500 flex items-center gap-1">
            <Check className="h-3 w-3 text-emerald-600" /> a member here
          </span>
        ) : row.application_status ? (
          <span className="text-[11px] text-slate-500">
            application {row.application_status}
          </span>
        ) : row.invited ? (
          <span className="text-[11px] text-indigo-600 flex items-center gap-1"
                data-testid={`event-signup-invited-${row.delegate_id}`}>
            <Mail className="h-3 w-3" />
            {row.invite?.emailed ? 'invitation emailed' : 'invitation recorded'}
          </span>
        ) : (
          <Button size="sm" variant="outline" className="h-7 text-[11px]"
                  disabled={invite.isPending || !row.can_invite}
                  onClick={() => invite.mutate()}
                  data-testid={`event-signup-invite-${row.delegate_id}`}>
            {invite.isPending
              ? <Loader2 className="h-3 w-3 animate-spin" />
              : <><UserPlus className="h-3 w-3 mr-1" /> Invite to join</>}
          </Button>
        )}
      </div>
    </div>
  );
};

/** Attaching an event to this assembly, so its sign-ups reach this list. */
const TagEvent = ({ churchId, onTagged }) => {
  const [open, setOpen] = useState(false);
  const [term, setTerm] = useState('');
  const { data, isFetching } = useQuery({
    queryKey: ['church-taggable-events', churchId, term],
    queryFn: () => api(`/api/admin/churches/${churchId}/taggable-events`
      + (term ? `?q=${encodeURIComponent(term)}` : '')),
    enabled: open,
  });
  const tag = useMutation({
    mutationFn: (eventId) => api(
      `/api/admin/churches/${churchId}/taggable-events/${eventId}`, { method: 'POST' }),
    onSuccess: (r) => {
      toast.success(`${r.event_name || 'Event'} is now attached to this assembly`);
      setOpen(false);
      onTagged();
    },
    onError: (e) => toast.error(e.message),
  });

  return (
    <div data-testid="event-signups-tag">
      <Button size="sm" variant="ghost" className="h-8 text-xs"
              onClick={() => setOpen(!open)} data-testid="event-signups-tag-toggle">
        <LinkIcon className="h-3 w-3 mr-1" /> Attach an event
      </Button>
      {open && (
        <div className="mt-2 rounded-md border border-slate-200 p-3 space-y-2 bg-slate-50/60">
          <Input placeholder="Search events you can attach..." value={term}
                 onChange={(e) => setTerm(e.target.value)} className="h-8"
                 data-testid="event-signups-tag-search" />
          {isFetching ? (
            <Loader2 className="h-4 w-4 animate-spin mx-auto my-3" />
          ) : (data?.items || []).length === 0 ? (
            <p className="text-[11px] text-slate-500 py-2">
              No untagged events you have access to. An event you did not create has to be
              attached from its own workspace.
            </p>
          ) : (
            <div className="max-h-56 overflow-auto divide-y divide-slate-100">
              {data.items.map((e) => (
                <div key={e.event_id} className="flex items-center gap-2 py-1.5">
                  <div className="min-w-0 flex-1">
                    <div className="text-xs font-medium text-slate-900 truncate">{e.name}</div>
                    <div className="text-[10px] text-slate-500">
                      {e.start_date || 'no date'} · {e.delegates} signed up
                    </div>
                  </div>
                  <Button size="sm" variant="outline" className="h-6 text-[10px]"
                          disabled={tag.isPending}
                          onClick={() => tag.mutate(e.event_id)}
                          data-testid={`event-signups-tag-${e.event_id}`}>
                    Attach
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

/** What became of the invitations already sent. */
const FollowUp = ({ churchId }) => {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['church-signup-follow-up', churchId],
    queryFn: () => api(`/api/admin/churches/${churchId}/event-signups/follow-up`),
  });
  const setStatus = useMutation({
    mutationFn: ({ visitorId, status }) => api(
      `/api/admin/churches/${churchId}/visitors/${visitorId}`,
      { method: 'PATCH', body: JSON.stringify({ status }) }),
    onSuccess: () => {
      toast.success('Follow-up updated');
      qc.invalidateQueries({ queryKey: ['church-signup-follow-up', churchId] });
      qc.invalidateQueries({ queryKey: ['church-visitors', churchId] });
    },
    onError: (e) => toast.error(e.message),
  });
  if (isLoading) return <Loader2 className="h-4 w-4 animate-spin mx-auto my-6" />;
  const rows = data?.items || [];
  const counts = data?.counts || {};
  const STAGE = {
    joined: 'bg-emerald-100 text-emerald-800',
    applied: 'bg-indigo-100 text-indigo-800',
    'in touch': 'bg-sky-100 text-sky-800',
    quiet: 'bg-amber-100 text-amber-900',
    waiting: 'bg-slate-100 text-slate-600',
  };
  return (
    <div className="space-y-3" data-testid="event-signups-followup">
      <div className="flex flex-wrap gap-2 text-[11px]">
        <Badge variant="outline">{counts.invited || 0} invited</Badge>
        <Badge variant="outline">{counts.automatic || 0} sent automatically</Badge>
        <Badge variant="outline">{counts.applied || 0} applied</Badge>
        <Badge variant="outline">{counts.joined || 0} joined</Badge>
        <Badge variant="outline">{counts.quiet || 0} gone quiet</Badge>
      </div>
      {rows.length === 0 ? (
        <p className="text-sm text-slate-500 text-center py-6" data-testid="event-signups-followup-empty">
          No invitations sent yet.
        </p>
      ) : (
        <div className="divide-y divide-slate-100">
          {rows.map((r) => (
            <div key={r.visitor_id} className="flex flex-wrap items-center gap-3 py-2"
                 data-testid={`event-signups-followup-${r.visitor_id}`}>
              <div className="min-w-0 flex-1">
                <div className="text-sm text-slate-900 truncate flex items-center gap-1.5">
                  {r.name || '(no name)'}
                  {r.invite_source === 'auto' && (
                    <span className="text-[9px] uppercase tracking-wide text-indigo-600 border border-indigo-200 rounded px-1"
                          data-testid={`event-signups-auto-${r.visitor_id}`}>
                      automatic
                    </span>
                  )}
                </div>
                <div className="text-[11px] text-slate-500 truncate">
                  {r.event_name}
                  {r.days_since !== null ? ` · invited ${r.days_since} days ago` : ''}
                  {r.match_basis ? ` · matched by ${r.match_basis}` : ''}
                  {!r.emailed && r.email_note ? ` · ${r.email_note}` : ''}
                </div>
              </div>
              <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${STAGE[r.stage] || STAGE.waiting}`}>
                {r.stage}{r.application_status ? ` · ${r.application_status}` : ''}
              </span>
              <select
                value={r.status || 'new'}
                onChange={(e) => setStatus.mutate({ visitorId: r.visitor_id, status: e.target.value })}
                disabled={setStatus.isPending}
                className="h-7 rounded border border-slate-200 bg-white px-1.5 text-[11px] text-slate-700"
                data-testid={`event-signups-status-${r.visitor_id}`}
              >
                {['new', 'contacted', 'visited', 'joined', 'archived'].map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
              {r.link && (
                <Button size="sm" variant="ghost" className="h-6 text-[10px]"
                        onClick={() => {
                          navigator.clipboard?.writeText(r.link);
                          toast.success('Link copied');
                        }}
                        data-testid={`event-signups-copy-${r.visitor_id}`}>
                  <Copy className="h-3 w-3" />
                </Button>
              )}
            </div>
          ))}
        </div>
      )}
      <p className="text-[11px] text-slate-400">
        Registrants near this assembly are invited automatically when they sign
        up for an event. Use the list above to mark who you have reached, and
        the other tabs to invite anybody the match did not reach.
      </p>
    </div>
  );
};

export default function EventSignups({ churchId }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [show, setShow] = useState('not_invited');
  const [q, setQ] = useState('');
  const [picked, setPicked] = useState([]);

  const { data, isLoading } = useQuery({
    queryKey: ['church-event-signups', churchId, show, q],
    queryFn: () => api(`/api/admin/churches/${churchId}/event-signups?show=${show}`
      + (q ? `&q=${encodeURIComponent(q)}` : '')),
    enabled: open,
  });

  const counts = data?.counts || {};
  const items = data?.items || [];
  const refresh = () => {
    setPicked([]);
    qc.invalidateQueries({ queryKey: ['church-event-signups', churchId] });
    qc.invalidateQueries({ queryKey: ['church-signup-follow-up', churchId] });
  };

  const inviteMany = useMutation({
    mutationFn: () => api(`/api/admin/churches/${churchId}/event-signups/invite-many`, {
      method: 'POST', body: JSON.stringify({ delegate_ids: picked, send_email: true }),
    }),
    onSuccess: (r) => {
      const parts = [`${r.count} invited`];
      if (r.emailed) parts.push(`${r.emailed} emailed`);
      if (r.skipped?.length) parts.push(`${r.skipped.length} skipped`);
      toast.success(parts.join(' · '));
      if (r.not_emailed?.length) {
        toast.message(`${r.not_emailed.length} could not be emailed — their links are on the follow-up list`);
      }
      refresh();
    },
    onError: (e) => toast.error(e.message),
  });

  const selectable = items.filter((i) => i.can_invite).map((i) => i.delegate_id);
  const toggleOne = (id) => setPicked((p) =>
    p.includes(id) ? p.filter((x) => x !== id) : [...p, id]);

  return (
    <Card data-testid="event-signups-panel">
      <CardContent className="p-4">
        <button onClick={() => setOpen(!open)}
                className="w-full flex items-center gap-2 text-left"
                data-testid="event-signups-toggle">
          <CalendarCheck className="h-4 w-4 text-indigo-600 shrink-0" />
          <span className="font-medium text-slate-900 text-sm">
            People who came to our events
          </span>
          <span className="text-[11px] text-slate-500">
            invite them to join the church
          </span>
          <ChevronDown className={`h-4 w-4 text-slate-400 ml-auto transition-transform ${open ? 'rotate-180' : ''}`} />
        </button>

        {open && (
          <div className="mt-4 space-y-3">
            <div className="flex flex-wrap items-center gap-2">
              <div className="flex rounded-md border border-slate-200 overflow-hidden bg-white">
                {FILTERS.map((f) => (
                  <button
                    key={f.key} onClick={() => { setShow(f.key); setPicked([]); }}
                    data-testid={`event-signups-filter-${f.key}`}
                    className={`px-3 py-1.5 text-xs font-medium transition-colors ${
                      show === f.key ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-50'}`}
                  >
                    {f.label}
                    {f.key === 'not_invited' && counts.not_invited ? ` (${counts.not_invited})` : ''}
                    {f.key === 'invited' && counts.invited ? ` (${counts.invited})` : ''}
                    {f.key === 'already_here' && counts.already_here ? ` (${counts.already_here})` : ''}
                    {f.key === 'all' && counts.total ? ` (${counts.total})` : ''}
                  </button>
                ))}
              </div>
              <div className="relative max-w-xs flex-1 min-w-0 sm:min-w-[200px]">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input placeholder="Search name, email or event..." value={q}
                       onChange={(e) => setQ(e.target.value)} className="pl-10 h-9"
                       data-testid="event-signups-search" />
              </div>
              <TagEvent churchId={churchId} onTagged={refresh} />
            </div>

            {isLoading ? (
              <Loader2 className="h-5 w-5 animate-spin mx-auto my-8" />
            ) : show === 'follow_up' ? (
              <FollowUp churchId={churchId} />
            ) : items.length === 0 ? (
              <p className="text-sm text-slate-500 text-center py-8"
                 data-testid="event-signups-empty">
                {counts.total
                  ? 'Nobody in this group.'
                  : 'No event sign-ups reach this church yet. An event only appears here once it is tagged to this assembly — open the event, go to "Churches & provinces", and tag it. Its registrations then show up on this list.'}
              </p>
            ) : (
              <>
                {selectable.length > 0 && (
                  <div className="flex flex-wrap items-center gap-2 bg-slate-50 rounded-md px-3 py-2">
                    <Checkbox
                      checked={picked.length > 0 && picked.length === selectable.length}
                      onCheckedChange={() => setPicked(
                        picked.length === selectable.length ? [] : selectable)}
                      data-testid="event-signups-select-all" />
                    <span className="text-xs text-slate-600">
                      {picked.length > 0
                        ? `${picked.length} selected`
                        : `Select all ${selectable.length} on this list`}
                    </span>
                    {picked.length > 0 && (
                      <Button size="sm" className="h-7 text-[11px] ml-auto"
                              disabled={inviteMany.isPending}
                              onClick={() => inviteMany.mutate()}
                              data-testid="event-signups-invite-selected">
                        {inviteMany.isPending
                          ? <Loader2 className="h-3 w-3 animate-spin" />
                          : <><UserPlus className="h-3 w-3 mr-1" /> Invite {picked.length}</>}
                      </Button>
                    )}
                  </div>
                )}
                <div data-testid="event-signups-list">
                  {items.map((row) => (
                    <SignupRow key={row.delegate_id} row={row} churchId={churchId}
                               onInvited={refresh}
                               selected={picked.includes(row.delegate_id)}
                               onSelect={toggleOne} />
                  ))}
                </div>
                {data?.truncated && (
                  <p className="text-[11px] text-slate-400 text-center">
                    Showing the first {items.length} of {data.total_matching}. Search to narrow it down.
                  </p>
                )}
                <p className="text-[11px] text-slate-400 flex items-center gap-1">
                  <Copy className="h-3 w-3" />
                  An invitation emails them the join link and adds them to the
                  visitors list. They are only a member once they apply and you approve.
                </p>
              </>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
